import { hideBulletinDialog, bulletinDialogSaved } from '../store/actions/bulletinDialog.actions';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useForm, FormProvider, useWatch, useFormState } from 'react-hook-form';
import { Grid } from '@material-ui/core';
import RMSCommonDialog from '../../shared/components/dialog/RMSCommonDialog';
import { RMSConfirmationDialog } from '../../shared/components/dialog';
import RMSRecordHistoryButton from '../../shared/components/modules/RMSRecordHistoryButton';
import { RMSTextField, RMSSelect, RMSDateTimePicker } from '../../shared/components/form-controls';
import SearchPersonAutoComplete from '../../shared/components/modules/SearchPersonAutoComplete';
import SearchAddressAutoComplete from '../../shared/components/modules/SearchAddressAutoComplete';
import SearchVehicleAutoComplete from '../../shared/components/modules/SearchVehicleAutoComplete';
import { executeApiAction } from '../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../shared/utility/useConfirmDialog';
import { getDictionarySelectProps } from 'features/shared/utility/dictionarySelectHelper';
import {
  BULLETIN_DEFAULT_VALUES,
  PRIORITY_OPTIONS,
  PRIORITY_SELECT_PROPS,
} from '../constants/personBulletinFormConstants';
import useUserDictionaryOptions from '../hooks/useUserDictionaryOptions';
import { createBulletin, updateBulletin, getBulletin } from '../services/bulletinService';
import { useAddressDialog } from 'features/user/hooks/useAddressDialog';
import { getBulletinFormValues } from '../utility/bulletinFormHelper';

const AddEditBulletinDialog = ({ dialog }) => {
  const { personId, vehicleId = null, bulletinId = null, canSave = true, requestId } = dialog;
  const open = true;
  const dispatch = useDispatch();
  const onClose = useCallback(() => dispatch(hideBulletinDialog(requestId)), [dispatch, requestId]);
  const onSaved = useCallback(
    (bulletin) => {
      dispatch(bulletinDialogSaved(bulletin, requestId));
      dispatch(hideBulletinDialog(requestId));
    },
    [dispatch, requestId]
  );
  const { openAddressDialog } = useAddressDialog();
  const [submitting, setSubmitting] = useState(false);

  const isEditMode = Boolean(bulletinId);
  const [bulletin, setBulletin] = useState(null);
  const [loading, setLoading] = useState(Boolean(bulletinId));
  const [loadedBulletinId, setLoadedBulletinId] = useState(null);
  const canSubmit = canSave && (!isEditMode || loadedBulletinId === bulletinId);
  const { alertTypeOptions, agencyOptions, stateOptions } = useUserDictionaryOptions();

  const methods = useForm({
    mode: 'onSubmit',
    disabled: !canSubmit || loading,
    defaultValues: {
      ...BULLETIN_DEFAULT_VALUES,
      ptsPersonID: personId ?? null,
      ptsVehicleID: vehicleId,
    },
  });

  const { isDirty } = useFormState({ control: methods.control });
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();
  const ptsPersonID = useWatch({ control: methods.control, name: 'ptsPersonID' });
  const ptsAddressID = useWatch({ control: methods.control, name: 'ptsAddressID' });
  const ptsVehicleID = useWatch({ control: methods.control, name: 'ptsVehicleID' });
  const beginning = useWatch({ control: methods.control, name: 'Beginning' });
  const ending = useWatch({ control: methods.control, name: 'Ending' });

  const handlePersonCreated = useCallback(
    (person) => {
      methods.setValue('ptsPersonID', person?.ptsPersonID ?? null, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [methods]
  );

  const handleAddressCreated = useCallback(
    (address) => {
      methods.setValue('ptsAddressID', address?.ptsAddressID ?? null, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [methods]
  );

  const handleAlertTypeChange = useCallback(
    (e, option) => {
      methods.setValue('Flags', option?.Flags ?? null);
    },
    [methods]
  );

  const handleVehicleSelect = useCallback(
    (vehicle) => {
      if (!vehicle) return;

      if (vehicle.PlateNumber) methods.setValue('Plate', vehicle.PlateNumber);
      if (vehicle.PlateState) methods.setValue('PlateState', vehicle.PlateState);
    },
    [methods]
  );

  const handleVehicleCreated = useCallback(
    ({ vehicle, registration } = {}) => {
      methods.setValue('ptsVehicleID', vehicle?.ptsVehicleID ?? null, {
        shouldDirty: true,
        shouldValidate: true,
      });

      if (registration?.PlateNumber) methods.setValue('Plate', registration.PlateNumber);
      if (registration?.PlateState) methods.setValue('PlateState', registration.PlateState);
    },
    [methods]
  );

  const onSubmit = useCallback(
    async (data) => {
      if (!canSubmit || loading) return;
      const payload = {
        AlertType: data.AlertType || null,
        Flags: data.Flags || null,
        Beginning: data.Beginning || null,
        Ending: data.Ending || null,
        Notification: data.Notification || null,
        ptsPersonID: data.ptsPersonID || null,
        ptsAddressID: data.ptsAddressID || null,
        ptsVehicleID: data.ptsVehicleID || null,
        Plate: data.Plate || null,
        PlateState: data.PlateState || null,
        OLN: data.OLN || null,
        OLNState: data.OLNState || null,
        AgencyID: data.AgencyID || null,
        Priority: data.Priority || null,
        RangeOfEffectiveness: data.RangeOfEffectiveness || null,
      };

      await executeApiAction({
        setLoading: setSubmitting,
        dispatch,
        action: () => (isEditMode ? updateBulletin(bulletinId, payload) : createBulletin(payload)),
        onSuccess: (result) => {
          dispatch(
            notify(
              isEditMode ? 'Bulletin updated successfully' : 'Bulletin created successfully',
              'success'
            )
          );
          onSaved?.(result);
        },
      });
    },
    [dispatch, isEditMode, bulletinId, onSaved, canSubmit, loading]
  );

  const handleSave = methods.handleSubmit(onSubmit);

  const requestClose = useCallback(() => {
    if (isDirty) {
      requestConfirm(
        'You have unsaved changes on this bulletin. Are you sure you want to close without saving?',
        () => onClose?.(),
        'Unsaved Changes'
      );
      return;
    }

    onClose?.();
  }, [isDirty, requestConfirm, onClose]);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setBulletin(null);
    setLoadedBulletinId(null);
    methods.reset({
      ...BULLETIN_DEFAULT_VALUES,
      ptsPersonID: personId ?? null,
      ptsVehicleID: vehicleId,
    });
    if (!bulletinId) {
      setLoading(false);
      return;
    }
    executeApiAction({
      dispatch,
      setLoading: (value) => {
        if (active) setLoading(value);
      },
      action: () => getBulletin(bulletinId),
      onSuccess: (result) => {
        if (!active) return;
        const response = result?.data || result;
        const record = Array.isArray(response) ? response[0] : response;
        if (!record) return;
        methods.reset(getBulletinFormValues(record));
        setBulletin(record);
        setLoadedBulletinId(bulletinId);
      },
    });
    return () => {
      active = false;
    };
  }, [open, bulletinId, personId, vehicleId, dispatch, methods]);

  return (
    <RMSCommonDialog
      open={open}
      title={isEditMode ? (canSave ? 'Edit Bulletin' : 'View Bulletin') : 'Add Bulletin'}
      onClose={requestClose}
      onSave={handleSave}
      loading={submitting || loading}
      saveDisabled={submitting || loading || !canSubmit}
      maxWidth="md"
      fullWidth
      closeOnBackdropClick={false}
      saveButton={{
        show: true,
        type: 'simple',
        label: 'Save',
        variant: 'contained',
        color: 'primary',
      }}
      cancelButton={{
        show: true,
        type: 'simple',
        label: 'Close',
        variant: 'outlined',
        color: 'secondary',
      }}
      leftActions={
        isEditMode && (
          <RMSRecordHistoryButton
            disabled={loading || !bulletin?.Created}
            created={bulletin?.Created}
            createdBy={bulletin?.CreatedBy}
            updated={bulletin?.Updated}
            updatedBy={bulletin?.UpdatedBy}
          />
        )
      }>
      <FormProvider {...methods}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <RMSSelect
              name="AlertType"
              label="Bulletin Type"
              options={alertTypeOptions}
              {...getDictionarySelectProps()}
              onChange={handleAlertTypeChange}
            />
          </Grid>
          <Grid item xs={8}>
            <RMSSelect
              name="AgencyID"
              label="Agency"
              options={agencyOptions}
              {...getDictionarySelectProps({ optionValue: 'AgencyID' })}
            />
          </Grid>
          <Grid item xs={4}>
            <RMSSelect
              name="Priority"
              label="Priority"
              options={PRIORITY_OPTIONS}
              {...PRIORITY_SELECT_PROPS}
            />
          </Grid>
          <Grid item xs={6}>
            <RMSDateTimePicker
              name="Beginning"
              disabled={!canSubmit || loading}
              label="Date From"
              maxDate={ending || undefined}
            />
          </Grid>
          <Grid item xs={6}>
            <RMSDateTimePicker
              name="Ending"
              disabled={!canSubmit || loading}
              label="Date To"
              minDate={beginning || undefined}
            />
          </Grid>
          <Grid item xs={12}>
            <RMSTextField
              name="RangeOfEffectiveness"
              label="Range of Effectiveness"
              inputProps={{ maxLength: 50 }}
            />
          </Grid>
          <Grid item xs={12}>
            <SearchPersonAutoComplete
              name="ptsPersonID"
              label="Person"
              defaultPersonId={ptsPersonID}
              disabled={!canSubmit || loading}
              onPersonCreated={handlePersonCreated}
              showDetailsIcon={false}
            />
          </Grid>
          <Grid item xs={12}>
            <SearchAddressAutoComplete
              name="ptsAddressID"
              label="Search Address"
              defaultAddressId={ptsAddressID}
              disabled={!canSubmit || loading}
              onAddIconClick={() =>
                openAddressDialog(null, {
                  closeOnSave: true,
                  onAddressCreated: handleAddressCreated,
                })
              }
              showDetailsIcon={false}
            />
          </Grid>
          <Grid item xs={12}>
            <SearchVehicleAutoComplete
              name="ptsVehicleID"
              label="Search Vehicle"
              defaultVehicleId={ptsVehicleID}
              disabled={!canSubmit || loading}
              onSelectionChange={handleVehicleSelect}
              showAddIcon
              onVehicleCreated={handleVehicleCreated}
            />
          </Grid>
          <Grid item xs={6}>
            <RMSTextField name="Plate" label="Plate" inputProps={{ maxLength: 25 }} />
          </Grid>
          <Grid item xs={6}>
            <RMSSelect
              name="PlateState"
              label="Plate State"
              options={stateOptions}
              {...getDictionarySelectProps()}
            />
          </Grid>
          <Grid item xs={6}>
            <RMSTextField name="OLN" label="OLN" inputProps={{ maxLength: 50 }} />
          </Grid>
          <Grid item xs={6}>
            <RMSSelect
              name="OLNState"
              label="OLN State"
              options={stateOptions}
              {...getDictionarySelectProps()}
            />
          </Grid>
          <Grid item xs={12}>
            <RMSTextField name="Notification" label="Notes" multiline rows={4} />
          </Grid>
        </Grid>
      </FormProvider>

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </RMSCommonDialog>
  );
};

export default AddEditBulletinDialog;
